import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { styled } from '@material-ui/core/styles';
import './DashboardDisCoverModeStyle.css';
import DashboardDiscoverModeOfferActions from './DashboardDiscoverModeOfferActions';
import DashboardDiscoverModeProfileCard from './components/DashboardDiscoverModeProfileCard';
import DashboardDiscoverModeVideoPreviewLightBox from './components/DashboardDiscoverModeVideoPreviewLightBox';
import DashboardDiscoverModeTutorialWithoutSwipe from './DashboardDiscoverModeTutorialWithoutSwipe';
import DashboardDiscoverModeTutorial from './DashboardDiscoverModeTutorial';
import { AccountMaker, AccountDataObject, Account } from '../../../models/Account';
import { getSwipeModeAccounts, swipeModePassAccount } from '../../../sdk/ProfileSDK';
import { createNewOffer } from '../../../actions/offerActions';
import { connect } from 'react-redux';
import DashboardDiscoverModeNoMoreProfilesScreen from './DashboardDiscoverModeNoMoreProfilesScreen';
import DashboardDiscoverModePassFlag from './components/DashboardDiscoverModePassFlag';
import DashboardDiscoverModeOfferSentFlag from './components/DashboardDiscoverModeOfferSentFlag';
import { updateAccountConfig } from '../../../actions/profileActions';
import {
  SWIPE_MODE_SHOW_OFFER_CONFIRMATION,
  SWIPE_MODE_SHOW_PASS_CONFIRMATION,
  SWIPE_MODE_SHOW_TUTORIAL,
} from '../../../models/AccountSettings';
import LoadingBackdrop from '../../../atoms/loader/LoadingBackdrop';
import useFirstSwipeConfirmationModal from './hooks/useFirstSwipeConfirmationModal';
import { Profile, ProfileMaker } from '../../../models/Profile';
import { EVENT_NAMES } from '../../../constants/mixpanel';
import useMixPanel from '../../../hooks/useMixPanel';
import TinderCard from '../../../packages/react-tinder-card';
import { isDiscoverModeSwipeEnabled } from '../../../utils/swipe';
import { useDashboardDiscoverMode } from '../../../contexts/DashboardDiscoverModeContext';
import DashboardDiscoverModePassFlagOverlay from './components/DashboardDiscoverModePassFlagOverlay';
import DashboardDiscoverModeOfferSentFlagOverlay from './components/DashboardDiscoverModeOfferSentFlagOverlay';
import TrackLoadingToMixpanel from '../../common/TrackLoadingToMixPanel';
import {
  resetDiscoverModeAccountsAction,
  setDiscoverModeAccountsAction,
  updateDiscoverModeAccountsAction,
} from '../../../actions/discoverModeActions';
import { useDisableXBodyElementScroll } from '../../../hooks/useDisableBodyScroll';
import { API, ICreateNewOfferProp, IUpdateAccountConfigProp } from './types/DashboardDiscoverModeV2';
import { CurrencySymbols } from './types';
import { AppDispatch, RootState } from '../../../store';
import { handleDiscoverModeVysionEvent } from '../../../utils/vysion/dashboardDiscover';
import { DiscoverModeClickVysionEventsEnum } from '../../../utils/vysion/dashboardDiscover/click';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import breakpoints from '../../../theme/material/breakpoints';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { useCommonModal } from 'contexts/ModalContext';

type AccountType = AccountDataObject & { status?: 'viewed' | 'offer_sent' };

interface Props {
  ownProfile: Profile;
  ownAccount: Account;
  createNewOfferProp: (payload: ICreateNewOfferProp) => void;
  updateAccountConfigProp: (payload: { key: string; state: boolean }) => void;
  discoverModeAccounts: AccountType[];
  setDiscoverModeAccounts: (accounts: AccountType[]) => void;
  resetDiscoverModeAccountsAction: () => void;
  updateDiscoverModeAccounts: (accounts: AccountType[]) => void;
  discoverModeAccountsExpiration: number;
  profile?: any;
}

const DashboardDiscoverModeV2Container = styled('div')({
  maxWidth: 'calc(100% - 20px)',
  margin: '0px auto',
  textAlign: 'center',
  [breakpoints.up(769)]: {
    maxWidth: 1400,
    margin: '0px auto',
  },
});

const NoMoreProfilesScreenContainer = styled('div')({
  maxWidth: 'calc(100% - 20px)',
  margin: '0 auto',
  textAlign: 'center',
  [breakpoints.up(769)]: {
    maxWidth: 'calc(100% - 200px)',
    margin: '100px auto',
  },
});

const DashboardDiscoverModeV2: React.FC<Props> = ({
  createNewOfferProp,
  ownProfile,
  ownAccount,
  updateAccountConfigProp,
  discoverModeAccounts,
  setDiscoverModeAccounts,
  resetDiscoverModeAccountsAction,
  updateDiscoverModeAccounts,
  discoverModeAccountsExpiration,
}: Props) => {
  const { isMobile } = useMediaBreakpoints();

  const defaultGlobalOfferPrice = ownProfile.defaultOfferAmount();
  const isSwipeEnabled = isDiscoverModeSwipeEnabled('dashboard_discover_swipe_enable');
  const { mixpanelData, resetMixpanelData, trackPageView } = useMixPanel();
  const { discoverActiveAccount, setDiscoverActiveAccount } = useDashboardDiscoverMode();

  const { showFirstSwipeLeftConfirmation, showFirstSwipeRightConfirmation, initDiscoverConfirmationModal } =
    useFirstSwipeConfirmationModal();

  const { setShowTooManyPendingOfferModal, setModalArgs } = useCommonModal();
  const { showMandatoryWall } = useMandatoryWall();

  // Memoize the updateDiscoverModeAccounts function to prevent infinite re-renders
  const memoizedUpdateDiscoverModeAccounts = useCallback(
    (accounts: AccountType[]) => {
      updateDiscoverModeAccounts(accounts);
    },
    [updateDiscoverModeAccounts]
  );

  const [offerPrice, setOfferPrice] = useState<string | number | null>(defaultGlobalOfferPrice);
  const [isLoading, setIsLoading] = useState(true);
  const [showPassFlag, setShowPassFlag] = useState(false);
  const [isFulfilledSwipeLeft, setFulfilledSwipeLeft] = useState(false);
  const [isFulfilledSwipeRight, setFulfilledSwipeRight] = useState(false);
  const [showSentOfferFlag, setShowSentOfferFlag] = useState(false);
  const [showPassConfirmation, setShowPassConfirmation] = useState<undefined | boolean>(undefined);
  const [showOfferConfirmation, setShowOfferConfirmation] = useState<undefined | boolean>(undefined);
  const [hasError, setHasError] = useState(false);
  const [hasNoMoreResponse, setHasNoMoreResponse] = useState(false);
  const [disableManualClick, setDisableManualClick] = useState(false);
  const [autoSwiped, setAutoSwiped] = useState(false);
  const [accounts, setAccounts] = useState<AccountType[]>([]);
  const [currentIndex, setCurrentIndex] = useState(accounts.length - 1);
  const [ignoreList, setIgnoreList] = useState<string[]>([]);
  const [, setTriggerFrom] = useState<string>('swipe');

  const currentIndexRef = useRef(currentIndex);

  const hasAccountsToShow = useMemo(
    () => accounts.filter(account => account.status === undefined).length > 0,
    [accounts]
  );

  useEffect(() => {
    trackPageView('dashboard-swipe-mode');
  }, []);

  useDisableXBodyElementScroll();

  const childRefs = useMemo(
    () =>
      Array(accounts.length)
        .fill(0)
        .map(() => React.createRef<API>()),
    [accounts]
  );

  const currencySymbols: CurrencySymbols = useMemo(() => {
    let currencyInitial;
    let currencyLabelUnicode;

    if (ownAccount.data.currency) {
      currencyInitial = ownAccount.data.currency.data.initial;
      currencyLabelUnicode = ownAccount.data.currency.data.label_unicode;
    } else {
      currencyInitial = ownProfile && ownProfile?.profile?.currency?.data?.initial;
      currencyLabelUnicode = ownProfile && ownProfile?.profile?.currency?.data?.label_unicode;
    }

    return {
      currencyInitial,
      currencyLabel: currencyLabelUnicode ? String.fromCharCode(Number(currencyLabelUnicode)) : '',
    };
  }, [ownAccount, ownProfile]);

  const restoreCurrentIndexCard = () => {
    childRefs[currentIndex].current && (childRefs[currentIndex].current as API).restoreCard();
  };

  const updateCurrentIndex = (val: number) => {
    setCurrentIndex(val);
    currentIndexRef.current = val;
  };

  const getAccounts = async () => {
    setIsLoading(true);

    const ispreFetchExpired = discoverModeAccountsExpiration < new Date().getTime();

    if (
      !ispreFetchExpired &&
      typeof discoverModeAccounts !== 'undefined' &&
      discoverModeAccounts.length > 1 &&
      currentIndex !== 0
    ) {
      setHasNoMoreResponse(discoverModeAccounts.length < 1);
      setAccounts(discoverModeAccounts);
      setCurrentIndex(discoverModeAccounts.length - 1);
    } else {
      const response = await getSwipeModeAccounts();

      setHasNoMoreResponse(response?.data.data.length < 1);

      const filteredAccounts = response?.data.data.filter(
        (account: { profile: string | undefined }) => typeof account?.profile !== 'undefined'
      );
      const clonedAccounts = [...filteredAccounts];

      resetDiscoverModeAccountsAction();
      setDiscoverModeAccounts(clonedAccounts);
      setAccounts(clonedAccounts.reverse());
      updateCurrentIndex(clonedAccounts.length - 1);
    }

    setIsLoading(false);
    resetFulfilledSwipeFlags();
  };

  const updateOfferPrice = (val: number | string, isInvalidAmount: boolean) => {
    if (isInvalidAmount) {
      resetFulfilledSwipeFlags();
    }

    setHasError(isInvalidAmount);
    setDisableManualClick(false);
    setOfferPrice(val);
  };

  const resetFulfilledSwipeFlags = () => {
    setFulfilledSwipeLeft(false);
    setFulfilledSwipeRight(false);
    setShowPassFlag(false);
    setShowSentOfferFlag(false);
  };

  const passAccount = (accountHashId: string) => {
    const mxPayload = {
      ...mixpanelData[EVENT_NAMES.DISCOVER_MODE_ACTION],
      Result: 'Dismissed',
      'Offer Amount': 0,
    };

    removeAccountFromTheList(accountHashId);
    swipeModePassAccount(accountHashId);
    setHasError(false);

    setTimeout(() => {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.DISCOVER_MODE_ACTION,
        payload: {
          eventProperties: mxPayload,
        },
        callBack: () => resetMixpanelData(EVENT_NAMES.DISCOVER_MODE_ACTION),
      });
    }, 500);
  };

  const removeAccountFromTheList = (accountHashId: string) => {
    setIgnoreList(ignoreList => [...ignoreList, accountHashId]);
  };

  const offerAccount = async () => {
    if (!discoverActiveAccount || hasError) {
      return;
    }

    const defaultOfferAmount = discoverActiveAccount.profile?.data?.default_offer_amount;
    const mxPayload = {
      ...mixpanelData[EVENT_NAMES.DISCOVER_MODE_ACTION],
      Result: defaultOfferAmount === Number(offerPrice) ? 'Default Offer Sent' : 'Offer Sent',
      'Offer Amount': offerPrice,
    };

    removeAccountFromTheList(discoverActiveAccount.hash_id);
    setHasError(false);
    createNewOfferProp({
      account: discoverActiveAccount.hash_id,
      price: offerPrice,
      source: 'DashboardSwipeMode',
    });

    setTimeout(() => {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.DISCOVER_MODE_ACTION,
        payload: {
          eventProperties: mxPayload,
        },
        callBack: () => resetMixpanelData(EVENT_NAMES.DISCOVER_MODE_ACTION),
      });
    }, 500);
  };

  const onPassButtonClickHandler = async () => {
    if (typeof showPassConfirmation !== 'undefined' && showPassConfirmation == true) {
      showFirstSwipeLeftConfirmation(
        {
          onFirstSwipeLeftConfirmAction: doNotShowAgain => {
            if (doNotShowAgain) {
              updateAccountConfigProp({ key: SWIPE_MODE_SHOW_PASS_CONFIRMATION, state: false });
              setShowPassConfirmation(false);
            }

            if (childRefs[currentIndex] && currentIndex < accounts.length) {
              setAutoSwiped(true);
              setTimeout(async () => {
                await (childRefs[currentIndex].current as API).swipe('left'); // Swipe the card!
              }, 300);
            }
          },
          onFirstSwipeLeftCancelAction: () => {
            setDisableManualClick(false);
          },
        },
        discoverActiveAccount.username as string
      );
      return;
    }

    if (currentIndex < accounts.length) {
      setAutoSwiped(true);
      await (childRefs[currentIndex].current as API).swipe('left'); // Swipe the card!
    }
  };

  const onOfferButtonClickHandler = async () => {
    if (hasError) {
      return;
    }

    if (typeof showOfferConfirmation !== 'undefined' && showOfferConfirmation === true) {
      showFirstSwipeRightConfirmation(
        {
          onFirstSwipeRightConfirmAction: doNotShowAgain => {
            if (doNotShowAgain) {
              updateAccountConfigProp({ key: SWIPE_MODE_SHOW_OFFER_CONFIRMATION, state: false });
              setShowOfferConfirmation(false);
            }

            if (currentIndex < accounts.length) {
              setAutoSwiped(true);
              setTimeout(async () => {
                await (childRefs[currentIndex].current as API).swipe('right'); // Swipe the card!
              }, 300);
            }
          },
          onFirstSwipeRightCancelAction: () => {
            setDisableManualClick(false);
          },
        },
        ownProfile,
        discoverActiveAccount.username as string,
        offerPrice as number | string
      );

      return;
    }

    if (currentIndex < accounts.length) {
      setAutoSwiped(true);
      await (childRefs[currentIndex].current as API).swipe('right'); // Swipe the card!
    }
  };

  const onSwipedLeftHandler = (index: number) => {
    const updatedIndex = index - 1;

    if (typeof showPassConfirmation !== 'undefined' && showPassConfirmation === true && !autoSwiped) {
      restoreCurrentIndexCard();

      showFirstSwipeLeftConfirmation(
        {
          onFirstSwipeLeftConfirmAction: doNotShowAgain => {
            if (doNotShowAgain) {
              updateAccountConfigProp({ key: SWIPE_MODE_SHOW_PASS_CONFIRMATION, state: false });
              setShowPassConfirmation(false);
            }

            updateCurrentIndex(updatedIndex);
            setShowPassFlag(true);
            passAccount(discoverActiveAccount.hash_id);

            if (updatedIndex < 0) {
              setIsLoading(true);
              setTimeout(() => {
                getAccounts();
              }, 500);
            }
          },
          onFirstSwipeLeftCancelAction: () => {
            setDisableManualClick(false);
            restoreCurrentIndexCard();
          },
        },
        discoverActiveAccount.username as string
      );
      return;
    }

    updateCurrentIndex(updatedIndex);
    setShowPassFlag(true);
    setAutoSwiped(false);
  };

  const onSwipedRightHandler = (index: number) => {
    if (typeof showOfferConfirmation !== 'undefined' && showOfferConfirmation === true && !autoSwiped) {
      showFirstSwipeRightConfirmation(
        {
          onFirstSwipeRightConfirmAction: doNotShowAgain => {
            if (doNotShowAgain) {
              updateAccountConfigProp({ key: SWIPE_MODE_SHOW_OFFER_CONFIRMATION, state: false });
              setShowOfferConfirmation(false);
            }

            handleSendOffer(index);
          },
          onFirstSwipeRightCancelAction: () => {
            setDisableManualClick(false);
            restoreCurrentIndexCard();
          },
        },
        ownProfile,
        discoverActiveAccount.username as string,
        offerPrice as number | string
      );

      return;
    } else {
      handleSendOffer(index);
      setAutoSwiped(false);
    }
  };

  const handleSendOffer = index => {
    showMandatoryWall(
      () => {
        if (ownAccount.shouldShowTooManyLockedOffersDeals()) {
          setShowTooManyPendingOfferModal(true);
          setModalArgs({
            source: 'Discover Mode',
            onCloseCallback: () => {
              setDisableManualClick(false);
              restoreCurrentIndexCard();
            },
          });
          return;
        }

        if (ownAccount.shouldShowTooManySentOffers()) {
          setShowTooManyPendingOfferModal(true);
          setModalArgs({
            source: 'Discover Mode',
            onCloseCallback: () => {
              setDisableManualClick(false);
              restoreCurrentIndexCard();
            },
            type: 'sent',
          });
          return;
        }

        const updatedIndex = index - 1;

        updateCurrentIndex(updatedIndex);
        setShowSentOfferFlag(true);
        offerAccount();

        if (updatedIndex < 0) {
          setIsLoading(true);
          setTimeout(() => {
            getAccounts();
          }, 500);
        }
      },
      () => {
        restoreCurrentIndexCard();
      },
      'Request Offer'
    );
  };

  const swiped = (direction: 'left' | 'right' | 'up' | 'down', index: number) => {
    resetFulfilledSwipeFlags();

    if (direction === 'left') {
      onSwipedLeftHandler(index);

      return;
    }

    if (direction === 'right') {
      onSwipedRightHandler(index);

      return;
    }
  };

  const handleClickToSwipe = async (direction: 'left' | 'right') => {
    if (disableManualClick) return;

    setDisableManualClick(true);
    setTriggerFrom('click');

    if (direction === 'left') {
      onPassButtonClickHandler();
    } else if (direction === 'right') {
      onOfferButtonClickHandler();
    }
  };

  const handleOnCardLeftTheScreen = useCallback(
    (dir: string) => {
      if (dir === 'left' && !showPassConfirmation) {
        passAccount(discoverActiveAccount?.hash_id);

        if (currentIndex === 0) {
          setIsLoading(true);
          setTimeout(() => {
            getAccounts();
          }, 500);
        }
      }

      setDisableManualClick(false);

      if (dir === 'left') {
        setTriggerFrom(prev => {
          if (prev === 'click') {
            handleDiscoverModeVysionEvent({ event: DiscoverModeClickVysionEventsEnum.DISCOVER_TAP_DISMISS });
          } else {
            handleDiscoverModeVysionEvent({ event: DiscoverModeClickVysionEventsEnum.DISCOVER_SWIPE_DISMISS });
          }
          return 'swipe';
        });
      }

      if (dir === 'right') {
        setTriggerFrom(prev => {
          if (prev === 'click') {
            handleDiscoverModeVysionEvent({ event: DiscoverModeClickVysionEventsEnum.DISCOVER_TAP_OFFER });
          } else {
            handleDiscoverModeVysionEvent({ event: DiscoverModeClickVysionEventsEnum.DISCOVER_SWIPE_OFFER });
          }
          return 'swipe';
        });
      }
    },
    [currentIndex, discoverActiveAccount, showPassConfirmation, showOfferConfirmation, offerPrice, setTriggerFrom]
  );

  const handleSwipeRequirementFulfilled = useCallback(
    (dir: string) => {
      if (hasError) {
        resetFulfilledSwipeFlags();
        return;
      }

      setShowPassFlag(false);
      setShowSentOfferFlag(false);

      if (dir === 'left') {
        setFulfilledSwipeLeft(true);
        return;
      }

      if (dir === 'right') {
        setFulfilledSwipeRight(true);
        return;
      }

      resetFulfilledSwipeFlags();
    },
    [hasError]
  );

  const isCardVisible = useCallback(
    (index: number): boolean => {
      // we need to visible only 3 cards at a time
      // active card, previous card and next card
      return index === currentIndex || index === currentIndex - 1 || index === currentIndex + 1;
    },
    [currentIndex]
  );

  useEffect(() => {
    if (accounts.length > 0 && accounts[currentIndex]) {
      const otherAccount = accounts[currentIndex];

      setDiscoverActiveAccount({
        ...accounts[currentIndex],
        account: AccountMaker.create(otherAccount),
        profile: otherAccount.profile,
      });
      setOfferPrice(otherAccount.profile?.data?.default_offer_amount ?? defaultGlobalOfferPrice);
    }
  }, [currentIndex, accounts]);

  useEffect(() => {
    setShowPassConfirmation(ownAccount.data.account_config?.swipe_mode_show_pass_confirmation);
    setShowOfferConfirmation(ownAccount.data.account_config?.swipe_mode_show_offer_confirmation);
  }, [
    ownAccount.data.account_config?.swipe_mode_show_pass_confirmation,
    ownAccount.data.account_config?.swipe_mode_show_offer_confirmation,
  ]);

  useEffect(() => {
    if (accounts.length > 0 && ownProfile?.profile?.data?.hash_id && !isLoading) {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.DISCOVER_MODE_VIEWED,
        payload: {
          eventProperties: {
            'Number of Cards': accounts.length,
          },
        },
      });

      // page view mixpanel custom page name. will delete this after the trackPageView fired
      localStorage.setItem('mxTrackPageView_page_name', 'Discover Mode');
    }
  }, [accounts, ownProfile?.profile?.data?.hash_id, isLoading]);

  useEffect(() => {
    if (!hasAccountsToShow && !isLoading && hasNoMoreResponse && ownProfile?.profile?.data?.hash_id) {
      mixpanelTrackEvent({
        eventName: EVENT_NAMES.DISCOVER_HIT_ZERO,
        payload: {},
      });
    }
  }, [hasAccountsToShow, ownProfile?.profile?.data?.hash_id, isLoading, hasNoMoreResponse]);

  useEffect(() => {
    getAccounts();
  }, []);

  useEffect(() => {
    if (ignoreList && ignoreList.length > 0) {
      const filteredAccounts = accounts.filter(account => !ignoreList.includes(account.hash_id));

      // Only update if the filtered accounts are actually different
      if (filteredAccounts.length !== accounts.length) {
        memoizedUpdateDiscoverModeAccounts(filteredAccounts);
      }
    }
  }, [ignoreList, accounts, memoizedUpdateDiscoverModeAccounts]);

  if (isLoading) {
    return (
      <>
        <TrackLoadingToMixpanel page={`Discover Mode - loading profiles`} />
        <LoadingBackdrop active={true} showLoadingBg={false} />{' '}
      </>
    );
  }

  if (!hasAccountsToShow && !isLoading && hasNoMoreResponse) {
    return (
      <NoMoreProfilesScreenContainer>
        <DashboardDiscoverModeNoMoreProfilesScreen />
      </NoMoreProfilesScreenContainer>
    );
  }

  return (
    <DashboardDiscoverModeV2Container>
      {ownAccount.data.account_config?.swipe_mode_show_tutorial &&
        (isSwipeEnabled ? (
          <DashboardDiscoverModeTutorial
            onConfirm={() => updateAccountConfigProp({ key: SWIPE_MODE_SHOW_TUTORIAL, state: false })}
          />
        ) : (
          <DashboardDiscoverModeTutorialWithoutSwipe
            onConfirm={() => updateAccountConfigProp({ key: SWIPE_MODE_SHOW_TUTORIAL, state: false })}
          />
        ))}
      <div className="cardContainer">
        {accounts.map((account, index) => (
          <TinderCard
            ref={childRefs[index]}
            key={account.hash_id}
            className="swipe"
            onSwipe={dir => {
              swiped(dir, index);
            }}
            isVisible={isCardVisible(index)}
            preventSwipe={hasError ? ['up', 'down', 'right'] : ['up', 'down']}
            disableTouchSwipe={!isSwipeEnabled || discoverActiveAccount?.hash_id !== account.hash_id}
            swipeThreshold={isMobile ? 100 : 300}
            onCardLeftScreen={handleOnCardLeftTheScreen}
            swipeRequirementType="position"
            onSwipeRequirementFulfilled={handleSwipeRequirementFulfilled}
            onSwipeRequirementUnfulfilled={() => resetFulfilledSwipeFlags()}
            swipeRotation={isMobile ? 45 : 360}
            disabledAnimationY={true}
          >
            <DashboardDiscoverModeProfileCard
              key={account.hash_id}
              profile={account.profile.data}
              account={AccountMaker.create(account)}
            />
          </TinderCard>
        ))}

        {isFulfilledSwipeLeft && <DashboardDiscoverModePassFlagOverlay />}
        {showPassFlag && (
          <DashboardDiscoverModePassFlag
            onHidden={() => {
              setShowPassFlag(false);
            }}
          />
        )}
        {isFulfilledSwipeRight && <DashboardDiscoverModeOfferSentFlagOverlay currencySymbols={currencySymbols} />}
        {showSentOfferFlag && (
          <DashboardDiscoverModeOfferSentFlag
            onHidden={() => setShowSentOfferFlag(false)}
            currencySymbols={currencySymbols}
          />
        )}
      </div>
      <DashboardDiscoverModeVideoPreviewLightBox />
      {currentIndex >= 0 && (
        <DashboardDiscoverModeOfferActions
          offerPrice={Number(offerPrice)}
          updateOfferPrice={updateOfferPrice}
          swipe={handleClickToSwipe}
          hasError={hasError}
          currencySymbols={currencySymbols}
          disableManualClick={disableManualClick}
        />
      )}
      {initDiscoverConfirmationModal()}
    </DashboardDiscoverModeV2Container>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    createNewOfferProp: (payload: ICreateNewOfferProp) => {
      dispatch(createNewOffer(payload, false, null, null, false, '', true));
    },
    updateAccountConfigProp: (payload: IUpdateAccountConfigProp) => {
      dispatch(updateAccountConfig(payload));
    },
    setDiscoverModeAccounts: (accounts: AccountType[]) => {
      dispatch(setDiscoverModeAccountsAction(accounts));
    },
    resetDiscoverModeAccountsAction: () => {
      dispatch(resetDiscoverModeAccountsAction());
    },
    updateDiscoverModeAccounts: (accounts: AccountType[]) => {
      dispatch(updateDiscoverModeAccountsAction(accounts));
    },
  };
};

const mapStateToProps = (state: RootState) => {
  return {
    ownProfile: ProfileMaker.create(state.profile.profile),
    ownAccount: AccountMaker.create(state.profile),
    discoverModeAccounts: state.dashboard.DiscoverModeAccounts,
    discoverModeAccountsExpiration: state.dashboard.DiscoverModeAccountsExpiration,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardDiscoverModeV2);
